import { t } from "@lingui/core/macro";
import {
  addHours,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  getHours,
  getMinutes,
  isValid,
  isWithinInterval,
  parseISO,
} from "date-fns";

import { EMPTY_STRING } from "../constants/emptyString";
import type { ValuesUnion } from "../types/valuesUnion";
export const DateUnit = {
  DAY: "day" as const,
  WEEK: "week" as const,
  MONTH: "month" as const,
};

export const numOfDaysByUnit = {
  day: 1,
  week: 7,
  month: 30,
};

export const dateUnitOptions: Array<ValuesUnion<typeof DateUnit>> = [DateUnit.DAY, DateUnit.WEEK, DateUnit.MONTH];

export function getSecondsByUnitAndValue(value: number, unit: ValuesUnion<typeof DateUnit>) {
  return value * numOfDaysByUnit[unit] * 24 * 60 * 60;
}

/*
 * (krashanoff): Here, we take advantage of the `Date` type's built-in `setHours`. Previously,
 * we had used `date-fns`'s `endOfDay` function, but this produced inconsistent results since it
 * converts to and from the local timezone on a whim, even if using ISO strings.
 */
export function endOfDay(value: Date): Date {
  return timeOfDay(value, 23, 59);
}

/** Create a date object, by setting the HH:mm:ss:ms to 00:00:00.000. */
export function startOfDay(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
}

/** Create date object with HH and mm specified, and ss and ms defaulting to 59 and 999 . */
export function timeOfDay(value: Date, hours: number, minutes: number): Date {
  const result = new Date(value);
  result.setHours(hours, minutes, 59, 999);
  return result;
}

/** Returns true if the time in the date object passed is 11:59 PM. */
export function isEndOfDay(date: Date | string): boolean {
  const parsedDate = parseDate(date);
  return getHours(parsedDate) === 23 && getMinutes(parsedDate) === 59;
}

/** Returns true if the passed date is within the next num_hours, defaults to 24. */
export function isDateWithinNextHours(date: string | Date, num_hours = 24): boolean {
  const now = new Date();
  const endOfInterval = addHours(now, num_hours);
  const parsedDate = parseDate(date);
  return isWithinInterval(parsedDate, { start: now, end: endOfInterval });
}

/** Parses a given Date object or ISO string into a Date object. */
export function parseDate(date: Date | string): Date {
  if (date instanceof Date) {
    return parseISO(date.toISOString());
  }
  return parseISO(date);
}

export function isValidDate(date: unknown): date is Date {
  return date instanceof Date && !Number.isNaN(date);
}

/**
 * Returns the difference between date1 and date2.
 * E.g. getDifferenceInSeconds(3:45 PM, 3:46 PM) => -60 (seconds)
 * Note: Does not return the absolute difference.
 */
export function getDifferenceInSeconds(date1: Date | string, date2: Date | string): number {
  const date1Parsed = parseDate(date1);
  const date2Parsed = parseDate(date2);
  const differenceInMilliseconds = date1Parsed.getTime() - date2Parsed.getTime();
  return differenceInMilliseconds / 1000;
}

/**
 * Returns the relative difference between the given timestamp and the current date and time in a
 * human-readable format.
 * E.g. "1m" for 1 minute, "34m" for 34 minutes, "1h" for 1 hour, "5h" for 5 hours, "1d" for 1 day,
 * "55d" for 55 days, etc.
 */
export function getRelativeDifference(timestamp: string): string {
  const now = new Date();
  const parsedDate = parseISO(timestamp);

  const diffInMinutes = differenceInMinutes(now, parsedDate);
  if (diffInMinutes < 60) {
    return t({
      message: `${diffInMinutes}m`,
      comment: "Relative time distance in minutes with a single character as postfix to define the unit.",
    });
  }

  const diffInHours = differenceInHours(now, parsedDate);
  if (diffInHours < 24) {
    return t({
      message: `${diffInHours}h`,
      comment: "Relative time distance in hours with a single character as postfix to define the unit.",
    });
  }

  const diffInDays = differenceInDays(now, parsedDate);
  return t({
    message: `${diffInDays}d`,
    comment: "Relative time distance in days with a single character as postfix to define the unit.",
  });
}

/**
 * getDaysCompletedAheadOrBehindDueOn returns the number of days the provided completed at timestamp
 * is ahead or behind the provided due timestamp.
 */
export function getDaysCompletedAheadOrBehindDueOn(
  dueOn: string,
  completedAt: string,
): { type: "early" | "late"; value: number } | { type: "onTime" } {
  const parsedDueOn = parseISO(dueOn);
  const parsedCompletedAt = parseISO(completedAt);
  if (parsedCompletedAt <= parsedDueOn) {
    const daysDifference = differenceInDays(parsedDueOn, parsedCompletedAt);
    if (daysDifference === 0) {
      return { type: "onTime" };
    }
    return { type: "early", value: daysDifference };
  }
  const daysDifference = differenceInDays(parsedCompletedAt, parsedDueOn);
  if (daysDifference === 0) {
    return { type: "onTime" };
  }
  return { type: "late", value: daysDifference };
}

/**
 * Truncates a date object or ISO string to a date-only string.
 *
 * @param date - Date object or ISO string
 * @returns Date-only string
 */
export function truncateToDateString(date: Date | string): string {
  let parsedDate: Date | null;
  if (date instanceof Date) {
    parsedDate = date;
  } else {
    // (hchen): Check if the date string is a valid ISO string.
    parsedDate = parseISO(date);
    if (!isValidDate(parsedDate)) {
      console.warn(`Unable to truncate ${date} to a date string because it is not a valid ISO string.`);
      return EMPTY_STRING;
    }
    const datePart = date.split("T")[0];
    parsedDate = extractDateOnlyValue(datePart);
    if (parsedDate == null) {
      console.warn(`Unable to truncate ${date} to a date string`);
      return EMPTY_STRING;
    }
  }

  const year = parsedDate.getFullYear();
  const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0"); // Pad month to 2 digits
  const day = parsedDate.getDate().toString().padStart(2, "0"); // Pad day to 2 digits

  return `${year}-${month}-${day}`;
}

/**
 * Extracts a date-only value from a date or date string.
 *
 * @param date - Date object or date string
 * @returns Date object in the local timezone with time set to midnight,
 *   effectly truncating the date to the date only value.
 */
export function extractDateOnlyValue(date: Date | string | null | undefined): Date | null {
  if (date == null) {
    return null;
  }
  if (date instanceof Date) {
    date.setHours(0, 0, 0, 0);
    return date;
  }
  const validDateStringFormatRegex = /^\d{4}-\d{1,2}-\d{1,2}$/;
  if (!validDateStringFormatRegex.test(date)) {
    console.warn(`Invalid date string format: ${date}`);
    return null;
  }

  // (hchen): Assuming dateString is returned in "YYYY-MM-DD" format, this is denoted in
  // types.date on the backend.
  const [year, month, day] = date.split("-").map(Number);

  // (swerner)Note:
  // new Date("2023-10-15") is parsed as UTC
  // new Date(2023, 9, 15) is parsed as local time

  // (hchen): Create a Date object using the extracted year, month, and day insteand of `new
  // Date(dateString)` to avoid it trying to be clever and converting the date to the local
  // timezone Note: Month is 0-indexed in JavaScript Date, so subtract 1 from the month
  const parsedDate = new Date(year, month - 1, day);
  if (!isValid(parsedDate)) {
    return null;
  }
  return parsedDate;
}

/**
 * Converts a date object or ISO string to a local ISO string.
 *
 * @param date - Date object or ISO string
 * @returns Local ISO string
 */
export function convertToLocalISOString(date: Date | string): string {
  let parsedDate: Date | null;
  if (date instanceof Date) {
    parsedDate = date;
  } else {
    parsedDate = parseISO(date);
    if (!isValid(parsedDate)) {
      console.warn(`Unable to convert ${date} to a local ISO string because it is not a valid ISO string.`);
      return EMPTY_STRING;
    }
  }

  const yearPart = parsedDate.getFullYear();
  const monthPart = pad2(parsedDate.getMonth() + 1);
  const datePart = pad2(parsedDate.getDate());

  const hourPart = pad2(parsedDate.getHours());
  const minutePart = pad2(parsedDate.getMinutes());
  const secondPart = pad2(parsedDate.getSeconds());
  const milliPart = String(parsedDate.getMilliseconds()).padStart(3, "0");

  const fullDatePart = `${yearPart}-${monthPart}-${datePart}`;
  const fullTimePart = `${hourPart}:${minutePart}:${secondPart}.${milliPart}`;
  const timezonePart = getClientTimezoneOffset();

  return `${fullDatePart}T${fullTimePart}${timezonePart}`;
}

/**
 * Returns a string of the form "-07:00", "+00:00", etc., indicating the client's timezone offset.
 */
function getClientTimezoneOffset() {
  // (hchen): The ISO string must carry the timezone offset to be correctly handled by the
  // backend. When evaluating date field constraints such as "must be on or before today", the
  // stored value should be compared against EOD in the client timezone. It won't make sense to
  // compare a UTC timestamp against EOD in UTC, nor comparing a UTC timestamp with EOD in server
  // timezone.
  const date = new Date();
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";

  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;

  return `${sign}${pad2(offsetHours)}:${pad2(offsetMinutes)}`;
}

/** Pads the provided values to 2 digits with a leading "0" if needed. */
function pad2(num: number) {
  return num.toString().padStart(2, "0");
}
